import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createChannel,
  getChannel,
  updateChannel,
  SECRET_ARN_REGEX,
  type Channel,
  type ChannelCreate,
  type ChannelPatch,
  type ChannelStatus,
  type ChannelType,
} from '../api/channels.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const CHANNEL_TYPES: ChannelType[] = [
  'whatsapp',
  'webchat',
  'messenger',
  'instagram',
  'five9',
];
const STATUSES: ChannelStatus[] = ['connected', 'disconnected', 'error'];
const TYPES_WITH_PHONE = new Set<ChannelType>(['whatsapp', 'messenger', 'instagram']);

interface FormValues {
  name: string;
  channelType: ChannelType;
  phoneId: string;
  displayPhoneNumber: string;
  verifiedName: string;
  domain: string;
  status: ChannelStatus;
  tokenSecretArn: string;
}

const emptyValues: FormValues = {
  name: '',
  channelType: 'whatsapp',
  phoneId: '',
  displayPhoneNumber: '',
  verifiedName: '',
  domain: '',
  status: 'connected',
  tokenSecretArn: '',
};

/**
 * Zod schema. Aplica regex ARN. Lo usamos manualmente en onSubmit (sin
 * resolver externo) — react-hook-form ya soporta validate per-field también.
 */
export const channelFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  channelType: z.enum(['whatsapp', 'webchat', 'messenger', 'instagram', 'five9']),
  phoneId: z.string().optional(),
  displayPhoneNumber: z.string().optional(),
  verifiedName: z.string().optional(),
  domain: z.string().optional(),
  status: z.enum(['connected', 'disconnected', 'error']),
  tokenSecretArn: z
    .string()
    .optional()
    .refine(
      (v) => !v || SECRET_ARN_REGEX.test(v),
      { message: 'ARN_INVALID' },
    ),
});

function valuesToCreate(v: FormValues): ChannelCreate {
  const out: ChannelCreate = { name: v.name.trim(), channelType: v.channelType };
  if (v.status) out.status = v.status;
  if (v.tokenSecretArn) out.tokenSecretArn = v.tokenSecretArn;
  if (TYPES_WITH_PHONE.has(v.channelType)) {
    if (v.phoneId) out.phoneId = v.phoneId;
    if (v.displayPhoneNumber) out.displayPhoneNumber = v.displayPhoneNumber;
    if (v.verifiedName) out.verifiedName = v.verifiedName;
  } else if (v.channelType === 'webchat') {
    if (v.domain) out.domain = v.domain;
  }
  return out;
}

function buildPatch(current: Channel, v: FormValues): ChannelPatch {
  const patch: ChannelPatch = {};
  const trimmedName = v.name.trim();
  if (trimmedName !== current.name) patch.name = trimmedName;
  if (v.status !== current.status) patch.status = v.status;
  if ((v.tokenSecretArn || undefined) !== (current.tokenSecretArn ?? undefined)) {
    patch.tokenSecretArn = v.tokenSecretArn || undefined;
  }
  if (TYPES_WITH_PHONE.has(v.channelType)) {
    if ((v.phoneId || undefined) !== (current.phoneId ?? undefined)) {
      patch.phoneId = v.phoneId || undefined;
    }
    if ((v.displayPhoneNumber || undefined) !== (current.displayPhoneNumber ?? undefined)) {
      patch.displayPhoneNumber = v.displayPhoneNumber || undefined;
    }
    if ((v.verifiedName || undefined) !== (current.verifiedName ?? undefined)) {
      patch.verifiedName = v.verifiedName || undefined;
    }
  } else if (v.channelType === 'webchat') {
    if ((v.domain || undefined) !== (current.domain ?? undefined)) {
      patch.domain = v.domain || undefined;
    }
  }
  return patch;
}

export interface ChannelFormProps {
  mode: 'create' | 'edit';
}

export function ChannelForm({ mode }: ChannelFormProps): JSX.Element {
  const { id = '', channelId = '' } = useParams<{ id: string; channelId?: string }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const channelQuery = useQuery({
    queryKey: ['channel', id, channelId],
    queryFn: () => getChannel(id, channelId),
    enabled: mode === 'edit' && Boolean(id && channelId),
  });

  const defaultValues = useMemo<FormValues>(() => {
    if (mode === 'edit' && channelQuery.data) {
      const c = channelQuery.data;
      return {
        name: c.name,
        channelType: c.channelType,
        phoneId: c.phoneId ?? '',
        displayPhoneNumber: c.displayPhoneNumber ?? '',
        verifiedName: c.verifiedName ?? '',
        domain: c.domain ?? '',
        status: c.status,
        tokenSecretArn: c.tokenSecretArn ?? '',
      };
    }
    return emptyValues;
  }, [mode, channelQuery.data]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const watchedType = watch('channelType');

  const createMutation = useMutation({
    mutationFn: (body: ChannelCreate) => createChannel(id, body),
    onSuccess: () => {
      pushToast('success', t.channel.created);
      queryClient.invalidateQueries({ queryKey: ['channels', id] });
      navigate(`/tenants/${id}/channels`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: ChannelPatch) => {
      const current = channelQuery.data;
      if (!current) throw new Error('channel_not_loaded');
      return updateChannel(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.channel.updated);
      queryClient.invalidateQueries({ queryKey: ['channels', id] });
      queryClient.invalidateQueries({ queryKey: ['channel', id, channelId] });
      navigate(`/tenants/${id}/channels`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.channel.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const onSubmit = handleSubmit((values) => {
    const parsed = channelFormSchema.safeParse(values);
    if (!parsed.success) {
      pushToast('error', parsed.error.issues[0]?.message ?? 'invalid');
      return;
    }
    if (mode === 'create') {
      createMutation.mutate(valuesToCreate(values));
    } else if (channelQuery.data) {
      updateMutation.mutate(buildPatch(channelQuery.data, values));
    }
  });

  if (mode === 'edit' && channelQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && channelQuery.error) {
    return <p className="text-red-700">{(channelQuery.error as Error).message}</p>;
  }

  const submitting = isSubmitting || createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.channel.newTitle : t.channel.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.channel.fields.name} error={errors.name?.message}>
          <input
            type="text"
            {...register('name', {
              required: t.channel.nameRequired,
              maxLength: { value: 120, message: t.channel.nameTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field label={t.channel.fields.channelType} error={errors.channelType?.message}>
          <select
            {...register('channelType', { required: t.channel.typeRequired })}
            disabled={mode === 'edit'}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
          >
            {CHANNEL_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </select>
        </Field>

        {TYPES_WITH_PHONE.has(watchedType) && (
          <>
            <Field label={t.channel.fields.phoneId} error={errors.phoneId?.message}>
              <input
                type="text"
                {...register('phoneId')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field
              label={t.channel.fields.displayPhoneNumber}
              error={errors.displayPhoneNumber?.message}
            >
              <input
                type="text"
                {...register('displayPhoneNumber')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field
              label={t.channel.fields.verifiedName}
              error={errors.verifiedName?.message}
            >
              <input
                type="text"
                {...register('verifiedName')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
          </>
        )}

        {watchedType === 'webchat' && (
          <Field label={t.channel.fields.domain} error={errors.domain?.message}>
            <input
              type="text"
              placeholder="iplacex.cl"
              {...register('domain')}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
        )}

        {watchedType === 'five9' && (
          <div className="rounded border border-cyan-300 bg-cyan-50 p-3 text-xs text-cyan-900">
            <p className="font-medium">{t.channel.five9Title}</p>
            <p className="mt-1">{t.channel.five9Desc}</p>
            <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-[10px] text-slate-700">
{`{
  "apiKey": "f9-XXXXXXXX...",
  "baseUrl": "https://app-atl.five9.com",
  "campaign": "Chat"
}`}
            </pre>
            <p className="mt-2 text-[11px]">
              {t.channel.five9HandoffHint}
            </p>
          </div>
        )}

        <Field label={t.channel.fields.status} error={errors.status?.message}>
          <select
            {...register('status')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {STATUSES.map((st) => (
              <option key={st} value={st}>
                {t.channel.status[st]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t.channel.fields.tokenSecretArn}
          error={errors.tokenSecretArn?.message}
          helper={t.channel.arnHelper}
        >
          <input
            type="text"
            title={t.channel.arnExample}
            placeholder={t.channel.arnExample}
            {...register('tokenSecretArn', {
              validate: (v) =>
                !v || SECRET_ARN_REGEX.test(v) ? true : t.channel.arnInvalid,
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}/channels`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? '…' : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  helper,
  children,
}: {
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && !error && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}
